import { useEffect, useState } from "react";
import { TrendingUp, Activity, Users, Eye, Trash2 } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api } from "@/lib/api";

const FUNNEL_ORDER = [
  ["pageview", "Page Views", Eye],
  ["ai_readiness_started", "AI Worksheet Started", Activity],
  ["ai_readiness_completed", "AI Worksheet Completed", Activity],
  ["ai_pdf_downloaded", "AI PDF Downloaded", Activity],
  ["funding_readiness_started", "Funding Worksheet Started", Activity],
  ["funding_readiness_completed", "Funding Worksheet Completed", Activity],
  ["funding_pdf_downloaded", "Funding PDF Downloaded", Activity],
  ["discovery_inquiry_submitted", "Discovery Inquiry Submitted", Users],
  ["contact_submitted", "Contact Form Submitted", Users],
  ["reality_check_clicked", "CEO Reality Check Clicked", Activity],
];

export default function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  const load = async () => {
    const r = await api.get(`/admin/analytics?days=${days}`);
    setData(r.data);
  };

  useEffect(() => {
    load().catch(() => {});
  }, [days]);

  const reset = async () => {
    if (!confirm("Reset ALL analytics events? This cannot be undone.")) return;
    await api.delete("/admin/events");
    await load();
    toast.success("Events reset");
  };

  if (!data) return <div className="p-12 text-center text-[var(--tmg-muted)]">Loading analytics…</div>;

  const eventMap = Object.fromEntries(data.events_summary.map((e) => [e.type, e]));

  return (
    <div className="p-8 sm:p-10 space-y-10" data-testid="analytics-panel">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="tmg-overline mb-2">— Site Analytics</p>
          <h2 className="font-display text-2xl font-semibold">
            Last {days} days · {data.total_pageviews.toLocaleString()} pageviews · {data.total_events.toLocaleString()} events
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="analytics-range"
            className="border border-[var(--tmg-line)] px-3 py-2 text-sm bg-white"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 365 days</option>
          </select>
          <button onClick={reset} data-testid="reset-events-btn" className="text-xs font-semibold text-[var(--tmg-muted)] hover:text-red-600 uppercase tracking-[0.14em] flex items-center gap-1">
            <Trash2 className="w-3.5 h-3.5" /> Reset
          </button>
        </div>
      </div>

      {/* Daily pageviews chart */}
      <div className="border border-[var(--tmg-line)] bg-white p-6">
        <p className="tmg-overline mb-4">— Daily Pageviews</p>
        {data.daily_pageviews.length === 0 ? (
          <div className="h-48 flex items-center justify-center text-[var(--tmg-muted)] text-sm">
            No data yet. Once visitors hit the site, daily views will chart here.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.daily_pageviews} margin={{ top: 8, right: 12, left: -10, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E2D5" />
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#5C5C5C" }} />
              <YAxis tick={{ fontSize: 10, fill: "#5C5C5C" }} />
              <Tooltip
                contentStyle={{ background: "#0A0A0A", border: "1px solid #D4AF37", color: "#fff" }}
                labelStyle={{ color: "#D4AF37", fontSize: 12 }}
              />
              <Line type="monotone" dataKey="views" stroke="#D4AF37" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Conversion funnel */}
      <div className="border border-[var(--tmg-line)] bg-white p-6">
        <p className="tmg-overline mb-4">— Conversion Funnel</p>
        <div className="space-y-3">
          {FUNNEL_ORDER.map(([type, label, Icon]) => {
            const e = eventMap[type] || { count: 0, unique_sessions: 0 };
            const top = eventMap.pageview?.unique_sessions || 1;
            const pct = type === "pageview" ? 100 : Math.round((e.unique_sessions / top) * 100);
            return (
              <div key={type} data-testid={`funnel-row-${type}`} className="border border-[var(--tmg-line)] p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Icon className="w-4 h-4 text-[var(--tmg-gold)]" />
                    <span className="font-display text-base font-medium">{label}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs font-mono-sm">
                    <Badge variant="outline" className="border-[var(--tmg-gold)] text-[var(--tmg-gold-soft)]">
                      {e.unique_sessions} unique
                    </Badge>
                    <span className="text-[var(--tmg-muted)]">{e.count} total</span>
                  </div>
                </div>
                <div className="h-1 bg-[var(--tmg-line)] overflow-hidden">
                  <div className="h-full bg-[var(--tmg-gold)] transition-all duration-1000" style={{ width: `${Math.min(pct, 100)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top pages */}
      <div className="border border-[var(--tmg-line)] bg-white p-6">
        <p className="tmg-overline mb-4">— Top Pages</p>
        {data.top_pages.length === 0 ? (
          <p className="text-sm text-[var(--tmg-muted)]">No pageviews yet.</p>
        ) : (
          <div className="divide-y divide-[var(--tmg-line)]">
            {data.top_pages.map((p, i) => (
              <div key={p.path} data-testid={`page-row-${i}`} className="flex items-center justify-between py-3">
                <div className="flex items-center gap-4 min-w-0 flex-1">
                  <span className="font-mono-sm text-xs text-[var(--tmg-gold)] w-8">{String(i + 1).padStart(2, "0")}</span>
                  <code className="text-sm text-[var(--tmg-obsidian)] truncate">{p.path || "/"}</code>
                </div>
                <div className="flex items-center gap-6 text-sm flex-shrink-0">
                  <span className="text-[var(--tmg-muted)] font-mono-sm">{p.unique_sessions} unique</span>
                  <span className="font-display font-semibold w-12 text-right">{p.views}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
